import { Component, h, Host, Listen, Prop, State } from '@stencil/core';
import { FetchHelper } from '../../../globals/FetchHelper';
import { MarketWebContextService } from '../../../globals/MarketWebContextService';
import { translations } from './translations';
import { ProductGenericItem, ProxioPriceGroupi18n, ProxioPriceListGroupResponse, ProxioProduct, ProductWeightGroupListItem } from './types';
import { arrow_left, arrow_right } from 'pn-design-assets/pn-assets/icons.js';

@Component({
  tag: 'pn-proxio-pricegroup',
  styleUrl: 'pn-proxio-pricegroup.scss',
})
export class PnProxioPricegroup {
  /** Specifies which endpoint domain we should load from */
  @Prop({ mutable: true }) endpoint: string = null;
  //@Prop() culture: string = "sv-SE";
  @Prop() language: string = 'sv';
  @Prop() market: string = 'se';
  @Prop() tariffid: string = '';
  @Prop() cache: boolean = true;

  @State() activeWeightIndex: number = 0;
  @State() data: ProxioPriceListGroupResponse = null;
  @State() i18n: ProxioPriceGroupi18n = translations['sv'];

  fetchHelper = new FetchHelper('proxiopricegroup');

  componentWillLoad() {
    this.setInitialValues().then(() => {
      this.init();
    });
  }

  async setInitialValues(href = window.location.href) {
    const marketWebContextService = new MarketWebContextService(href);
    const environment = await marketWebContextService.getEnvironmentName();

    if (!this.endpoint && environment?.indexOf('local') === -1) {
      const market = await marketWebContextService.getMarket();
      this.endpoint = await marketWebContextService.getEndpoint(environment, market);
    }

    if (!this.endpoint) {
      this.endpoint = '';
    }
  }

  init() {
    this.setLanguage();
    this.fetchData();
  }

  async fetchData() {
    if (!this.market || !this.language || !this.tariffid) {
      return;
    }
    const endpoint = this.endpoint.lastIndexOf('/') === this.endpoint.length - 1 ? this.endpoint.substring(0, this.endpoint.length - 1) : this.endpoint;
    const fetchUrl = `${endpoint}/api/proxio/price-list-group?tariffId=${this.tariffid}&market=${this.market}&language=${this.language}&cached=${this.cache}`;
    //const fetchUrl = `https://localhost:51547/api/proxio/price-list-group?tariffId=${this.tariffid}&market=${this.market}&language=${this.language}&cached=${this.cache}`;
    const data = (await this.fetchHelper.fetchJson(
      fetchUrl,
      {
        mode: 'cors',
      },
      this.cache,
      this.onFetchData.bind(this),
    )) as any;

    if (data?.proxioProduct) {
      this.data = data;
    }
  }

  onFetchData(data) {
    this.data = data;
  }

  @Listen('activeWeightIndex')
  setActiveWeightIndex(weightIndex: number) {
    let minIndex = 0;
    let maxIndex = this.data.proxioProduct?.weightAndPriceList?.length - 1 ?? minIndex;
    weightIndex = weightIndex > maxIndex ? maxIndex : weightIndex;
    weightIndex = weightIndex < minIndex ? minIndex : weightIndex;
    this.activeWeightIndex = weightIndex;
  }

  @Listen('language')
  setLanguage() {
    if (translations[this.language]) {
      this.i18n = translations[this.language] as ProxioPriceGroupi18n;
    }
  }

  getPriceString(product: ProxioProduct) {
    let price = product.weightAndPriceList ? product?.weightAndPriceList[this.activeWeightIndex]?.item2 : '';
    return price ? this.i18n.from + price : '';
  }

  getProductLink(product: ProxioProduct, productWeightGroupList: ProductWeightGroupListItem[]) {
    let productId = productWeightGroupList[this.activeWeightIndex]?.item2;
    let productLink = product.productLink ? product?.productLink : '';
    return productLink ? productLink + (productId ? '?productId=' + productId : '') : '';
  }

  isActive(index: number) {
    return index === this.activeWeightIndex;
  }

  getCarouselPosition(index: number) {
    switch (index) {
      case this.activeWeightIndex - 2: {
        return -2;
      }
      case this.activeWeightIndex - 1: {
        return -1;
      }
      case this.activeWeightIndex: {
        return 0;
      }
      case this.activeWeightIndex + 1: {
        return 1;
      }
      case this.activeWeightIndex + 2: {
        return 2;
      }
      default:
        return 'outside';
    }
  }

  getContainerPosition() {
    let maxIndex = this.data.proxioProduct.weightAndPriceList.length - 1;
    switch (this.activeWeightIndex) {
      case 0: {
        return 'end50';
      }
      case 1: {
        return 'end75';
      }
      case maxIndex - 1: {
        return 'start75';
      }
      case maxIndex: {
        return 'start50';
      }
      default: {
        return 'default';
      }
    }
  }

  render() {
    return (
      <Host>
        {this.data?.proxioProduct ? (
          <div class="proxio-pricegroup-container">
            <div class="weightcontainer">
              {this.data.proxioProduct.weightAndPriceList ? (
                <div class="weightvaluecontainer">
                  <pn-button
                    disabled={this.activeWeightIndex === 0 ? 'disabled' : null}
                    icon={arrow_left}
                    appearance="light"
                    small="true"
                    onClick={() => this.setActiveWeightIndex(this.activeWeightIndex - 1)}
                  ></pn-button>
                  <div class="weightradios " data-carousel-container-position={this.getContainerPosition()}>
                    <div class="weightline"></div>
                    {this.data.proxioProduct.weightAndPriceList.map((weightItem, index) =>
                      this.getCarouselPosition(index) !== null ? (
                        <div class="weightdata" data-carousel-position={this.getCarouselPosition(index)} onClick={() => this.setActiveWeightIndex(index)}>
                          <input type="radio" id={'weightradio' + index} name="weightradio" value={index} checked={this.isActive(index)} />
                          <label htmlFor={'weightradio' + index}>{weightItem.item1}</label>
                        </div>
                      ) : null,
                    )}
                  </div>
                  <pn-button
                    disabled={this.activeWeightIndex === this.data.proxioProduct?.weightAndPriceList?.length - 1 ? 'disabled' : null}
                    icon={arrow_right}
                    appearance="light"
                    small="true"
                    onClick={() => this.setActiveWeightIndex(this.activeWeightIndex + 1)}
                  ></pn-button>
                </div>
              ) : null}
            </div>
            <pn-proxio-productcard>
              <pn-illustration slot="illustrationmobile" illustration={this.data.proxioProduct.productIcon}></pn-illustration>
              <h2 slot="title">{this.data.proxioProduct.productTitle}</h2>
              <pn-proxio-productcard-description
                slot="description"
                heading={this.data.proxioProduct.productPreamble}
                highlight={this.data.proxioProduct.deliveryDateInformation}
                list={this.data.proxioProduct.productDescriptionBulletListItems}
              ></pn-proxio-productcard-description>
              <div slot="information">
                <pn-proxio-productcard-information
                  label={this.i18n.labelMaxDimensions}
                  information={this.data.proxioProduct.productPackageDimensionsList as ProductGenericItem[]}
                ></pn-proxio-productcard-information>
                <pn-proxio-productcard-information
                  label={this.i18n.labelMaxDimensionsRoll}
                  information={this.data.proxioProduct.productRollDimensionsList as ProductGenericItem[]}
                ></pn-proxio-productcard-information>
              </div>
              <div slot="bottomtags">
                {this.data.proxioProduct.usp1 ? <pn-titletag color="gray50">{this.data.proxioProduct.usp1}</pn-titletag> : null}
                {this.data.proxioProduct.usp2 ? <pn-titletag color="gray50">{this.data.proxioProduct.usp2}</pn-titletag> : null}
                {this.data.proxioProduct.usp3 ? <pn-titletag color="gray50">{this.data.proxioProduct.usp3}</pn-titletag> : null}
              </div>
              <pn-illustration slot="illustration" illustration={this.data.proxioProduct.productIcon}></pn-illustration>
              <div slot="pricelink">
                <pn-proxio-productcard-pricelink
                  price={this.getPriceString(this.data.proxioProduct)}
                  link={this.getProductLink(this.data.proxioProduct, this.data.proxioProduct.productWeightGroupList)}
                  linktext={this.data.proxioProduct.productLinkText}
                ></pn-proxio-productcard-pricelink>
                {this.data.relatedProxioProducts
                  ? this.data.relatedProxioProducts.map(product => (
                      <pn-proxio-productcard-pricelink
                        price={this.getPriceString(product)}
                        link={product.productLink}
                        linktext={product.productLinkText}
                      ></pn-proxio-productcard-pricelink>
                    ))
                  : null}
              </div>
            </pn-proxio-productcard>
          </div>
        ) : null}
      </Host>
    );
  }
}
